import path from 'path';

import { Link as MuiLink } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { InfoCard, InfoCardRowDef } from '~components';
import { pagePathSegments } from '~constants';
import { useAuthorize } from '~features/authentication';
import { datetimeNoSecondsFormatter } from '~utils/dateUtils';

import { getCommunicationTypeLabel } from '../../../../shared/utility';

interface DeviceStatusCardProps {
	data: { device?: object };
}

/**
 * A card to show the status/heartbeat of a device.
 */
const DeviceInfoCard = ({ data }: DeviceStatusCardProps) => {
	const { t } = useTranslation();
	const location = useLocation();
	const { isSuperAdmin } = useAuthorize();

	const rows: InfoCardRowDef[] =
		data?.device != null ?
			[
				{
					headerName: t('ui.label.dateLinked'),
					value: new Date(data.device.dateLinked),
					valueFormatter: (value: Date) => datetimeNoSecondsFormatter.format(value),
				},
				{
					headerName: t('ui.label.HwUID'),
					value: data.deviceId,
					renderCell: (value: string) => (
						<MuiLink
							component={ReactRouterLink}
							to={`/${path.join(pagePathSegments.Devices, value, pagePathSegments.Summary)}`}
							state={{ from: location.pathname }}
						>
							{value}
						</MuiLink>
					),
				},
				{
					headerName: t('ui.label.deviceType'),
					value: data.device.deviceType,
					valueFormatter: (value: string) => (value === 'bmwCar' ? 'BMW' : t(`ui.label.${value}`)),
				},
				{
					headerName: t('ui.label.version'),
					value: data.device.hardwareVersion,
				},
				{
					headerName: t('ui.label.communication'),
					value: data.device.communicationType,
					valueFormatter: (value: string[]) =>
						value.map((el) => getCommunicationTypeLabel(el)).join(', ') || '-',
				},
				{
					headerName: t('views.organisationManagement.devices.unlockType'),
					value: data.device.identificationType,
					valueFormatter: (value: string[]) =>
						value.map((el) => getCommunicationTypeLabel(el)).join(', ') || '-',
				},
				{
					headerName: t('bluetoothName'),
					value: data.device.bluetoothName,
					hidden: !isSuperAdmin(),
				},
			]
		:	[];

	return <InfoCard title={t('ui.label.deviceInfo')} rows={rows} version='legacy' />;
};

export default DeviceInfoCard;
